import React from 'react';
import { i18n } from 'config/locales';

import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonOnError, CommonRequired
} from 'types/props';
import { ForeignBusinessVotingItemModel } from 'forms/sections/foreign/business/voting/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Country from 'components/Form/Country';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import { isPVQForm } from 'helpers/form';
import Branch from 'components/Form/Branch';

interface Props
  extends Partial<ForeignBusinessVotingItemModel>,
  ValidationElementProps,
  AccordionItemProps {
  onUpdate?: CommonOnUpdate
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  required?: CommonRequired
  formType?: string
}

export default class VotingItem extends ValidationElement<Props> {
  constructor(props) {
    super(props);
    this.updateCountryFocused = this.updateCountryFocused.bind(this);
  }

  update = (queue: Partial<ForeignBusinessVotingItemModel>) => {
    this.props.onUpdate({
      Date: this.props.Date,
      Country: this.props.Country,
      Reason: this.props.Reason,
      Eligibility: this.props.Eligibility,
      CountryFocused: this.props.CountryFocused,
      CurrentlyEligible: this.props.CurrentlyEligible,
      CurrentlyEligibleExplanation: this.props.CurrentlyEligibleExplanation,
      ...queue,
    });
  };

  updateDate = (values: ForeignBusinessVotingItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateCountry = (values: ForeignBusinessVotingItemModel['Country']) => {
    this.update({
      Country: values,
    });
  };

  updateReason = (values: ForeignBusinessVotingItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateEligibility = (values: ForeignBusinessVotingItemModel['Eligibility']) => {
    this.update({
      Eligibility: values,
    });
  };

  updateCountryFocused(focus: boolean) {
    this.update({
      CountryFocused: focus,
    });
  }

  updateCurrentlyEligible = (values: ForeignBusinessPoliticalItemModel['CurrentlyEligible']) => {
    this.update({
      CurrentlyEligible: values,
      CurrentlyEligibleExplanation: values?.value === 'Yes' ? this.props.CurrentlyEligibleExplanation : {},
    });
  };

  updateCurrentlyEligibleExplanation = (values: ForeignBusinessPoliticalItemModel['CurrentlyEligibleExplanation']) => {
    this.update({
      CurrentlyEligibleExplanation: values,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);

    return isPVQ ? (
      <div className="foreign-business-voting-item">
        <Field
          title={i18n.t('foreign.pvq.business.voting.heading.date')}
          help="foreign.business.voting.help.date"
          adjustFor="datecontrol"
          attribute={`${attribute}.Date`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            {...this.props.Date}
            name="Date"
            attribute={`${attribute}.Date`}
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="foreign-business-voting-date"
            required={this.props.required}
            hideDay
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.voting.heading.country')}
          adjustFor="country"
          attribute={`${attribute}.Country`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Country
            {...this.props.Country}
            name="Country"
            excludeUnitedStates
            attribute={`${attribute}.Country`}
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            className="foreign-business-voting-country"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.pvq.business.voting.heading.reason')}
          adjustFor="textarea"
          attribute={`${attribute}.Reason`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.Reason}
            name="Reason"
            attribute={`${attribute}.Reason`}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            className="foreign-business-voting-reason"
            required={this.props.required}
          />
        </Field>

        <Branch
          {...this.props.CurrentlyEligible}
          name="CurrentlyEligible"
          attribute={`${attribute}.CurrentlyEligible`}
          label={i18n.t('foreign.pvq.business.voting.heading.eligibility')}
          labelSize="h4"
          className="currently-eligible"
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateCurrentlyEligible}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.CurrentlyEligible?.value === 'Yes' && (
        <Field
          title={i18n.t('foreign.pvq.business.voting.heading.explanation')}
          attribute={`${attribute}.CurrentlyEligibleExplanation`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            {...this.props.CurrentlyEligibleExplanation}
            name={`${attribute}.CurrentlyEligibleExplanation`}
            attribute="CurrentlyEligibleExplanation"
            className="currently-eligible-explanation"
            onUpdate={this.updateCurrentlyEligibleExplanation}
            onError={this.props.onError}
            required={this.props.required}
          />
        </Field>
        )}
      </div>
    ) : (
      <div className="foreign-business-voting-item">
        <Field
          title={i18n.t('foreign.business.voting.heading.date')}
          help="foreign.business.voting.help.date"
          adjustFor="datecontrol"
          attribute={`${attribute}.Date`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateControl
            name="Date"
            attribute={`${attribute}.Date`}
            {...this.props.Date}
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="foreign-business-voting-date"
            required={this.props.required}
            hideDay
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.voting.heading.country')}
          adjustFor="country"
          attribute={`${attribute}.Country`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Country
            name="Country"
            {...this.props.Country}
            excludeUnitedStates
            attribute={`${attribute}.Country`}
            onUpdate={this.updateCountry}
            updateCountryFocused={this.updateCountryFocused}
            onError={this.props.onError}
            className="foreign-business-voting-country"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.voting.heading.reason')}
          adjustFor="textarea"
          attribute={`${attribute}.Reason`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Textarea
            name="Reason"
            attribute={`${attribute}.Reason`}
            {...this.props.Reason}
            onUpdate={this.updateReason}
            onError={this.props.onError}
            className="foreign-business-voting-reason"
            required={this.props.required}
          />
        </Field>

        <Field
          title={i18n.t('foreign.business.voting.heading.eligibility')}
          adjustFor="text"
          attribute={`${attribute}.Eligibility`}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Text
            name="Eligibility"
            {...this.props.Eligibility}
            attribute={`${attribute}.Eligibility`}
            onUpdate={this.updateEligibility}
            onError={this.props.onError}
            className="foreign-business-voting-eligibility"
            required={this.props.required}
          />
        </Field>
      </div>
    );
  }
}

VotingItem.defaultProps = {
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
  CountryFocused: false,
  CurrentlyEligible: {},
  CurrentlyEligibleExplanation: {},
};
